/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { User, Session } from '@supabase/supabase-js'
import {
  authService,
  LoginCredentials,
  RegisterCredentials,
} from '../../../features/auth/services/authService'
import { offlineStorageService } from '../../../features/sync/services/offlineStorage'

export interface AuthState {
  user: User | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  role: 'admin' | 'arborist' | 'client' | null
  // Increments on each successful login to trigger data re-fetching
  sessionVersion: number
  // The team the user most recently created work under (002). A creation
  // default only — never a filter on what the user sees. Persisted per
  // browser; null until org data loads, then falls back to the personal team.
  lastUsedTeamId: string | null
  // A recovery link has been opened and Supabase has established a session
  // for it. The session is real, so nothing stops it reaching the rest of the
  // app — the routes send it to /reset-password until the password is set.
  passwordRecovery: boolean
}

// Key name predates the rename (Phase 5c "active org"); kept verbatim so
// upgrading does not discard every user's persisted default
const LAST_USED_TEAM_STORAGE_KEY = 'arborstack.activeOrgId'

// localStorage is absent in non-browser test environments
const storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> =
  typeof localStorage !== 'undefined'
    ? localStorage
    : { getItem: () => null, setItem: () => undefined, removeItem: () => undefined }

const initialState: AuthState = {
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  role: null,
  sessionVersion: 0,
  lastUsedTeamId: storage.getItem(LAST_USED_TEAM_STORAGE_KEY),
  passwordRecovery: false,
}

/**
 * Async thunk for login
 */
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    const { user, session, error } = await authService.login(credentials)
    if (error) {
      return rejectWithValue(error.message)
    }

    // Fetch role from database
    let role: 'admin' | 'arborist' | 'client' | null = null
    if (user) {
      const { role: userRole } = await authService.getUserRole(user.id)
      role = userRole
    }

    return { user, session, role }
  }
)

/**
 * Async thunk for the portal's email-OTP login (Phase 6): verifies the
 * emailed code and resolves the role exactly like password login
 */
export const loginWithOtp = createAsyncThunk(
  'auth/loginWithOtp',
  async ({ email, token }: { email: string; token: string }, { rejectWithValue }) => {
    const { user, session, error } = await authService.verifyOtpCode(email, token)
    if (error) {
      return rejectWithValue(error.message)
    }

    let role: 'admin' | 'arborist' | 'client' | null = null
    if (user) {
      const { role: userRole } = await authService.getUserRole(user.id)
      role = userRole
    }

    return { user, session, role }
  }
)

/**
 * Async thunk for registration
 */
export const register = createAsyncThunk(
  'auth/register',
  async (credentials: RegisterCredentials, { rejectWithValue }) => {
    const { user, session, error } = await authService.register(credentials)
    if (error) {
      return rejectWithValue(error.message)
    }
    return { user, session }
  }
)

/**
 * Async thunk for logout
 */
export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  const { error } = await authService.logout()
  if (error) {
    return rejectWithValue(error.message)
  }

  // Clear all offline storage (IndexedDB) to prevent data leakage between users
  try {
    await offlineStorageService.clearAll()
  } catch (storageError) {
    console.error('Failed to clear offline storage:', storageError)
    // Don't fail logout if storage clear fails
  }

  return null
})

/**
 * Async thunk for checking session on app load
 */
export const checkSession = createAsyncThunk(
  'auth/checkSession',
  async (_, { rejectWithValue }) => {
    const { session, error } = await authService.getSession()
    if (error) {
      return rejectWithValue(error.message)
    }
    if (!session) {
      return null
    }
    const { user } = await authService.getUser()

    // Fetch role from database
    let role: 'admin' | 'arborist' | 'client' | null = null
    if (user) {
      const { role: userRole } = await authService.getUserRole(user.id)
      role = userRole
    }

    return { user, session, role }
  }
)

/**
 * Async thunk for password reset
 */
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email: string, { rejectWithValue }) => {
    const { error } = await authService.resetPassword(email)
    if (error) {
      return rejectWithValue(error.message)
    }
    return email
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * A recovery link was opened: Supabase has just established a session for
     * it. Cleared once the password is changed, or on sign-out.
     */
    setPasswordRecovery: (state, action: PayloadAction<boolean>) => {
      state.passwordRecovery = action.payload
    },

    /**
     * Set session from auth state change listener
     */
    setSession: (
      state,
      action: PayloadAction<{
        user: User | null
        session: Session | null
        role?: 'admin' | 'arborist' | 'client' | null
      }>
    ) => {
      state.user = action.payload.user
      state.session = action.payload.session
      state.isAuthenticated = !!action.payload.user
      state.role = action.payload.role || null
      state.isLoading = false

      // Always increment sessionVersion when setting a user session
      // This is called from onAuthStateChange when Supabase client has the session ready
      if (action.payload.user) {
        state.sessionVersion += 1
      }
    },
    /**
     * Clear auth error
     */
    clearError: (state) => {
      state.error = null
    },
    /**
     * Remember the team the user just created work under. Read only as the
     * default for the next creation (002) — nothing may treat it as a filter.
     */
    setLastUsedTeam: (state, action: PayloadAction<string | null>) => {
      state.lastUsedTeamId = action.payload
      if (action.payload) {
        storage.setItem(LAST_USED_TEAM_STORAGE_KEY, action.payload)
      } else {
        storage.removeItem(LAST_USED_TEAM_STORAGE_KEY)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user
        state.session = action.payload.session
        state.isAuthenticated = true
        state.role = action.payload.role
        state.isLoading = false
        state.error = null
        // Don't increment sessionVersion here - wait for setSession from onAuthStateChange
        // which fires after the Supabase client has the session token ready
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Portal email-OTP login (same lifecycle as password login)
      .addCase(loginWithOtp.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginWithOtp.fulfilled, (state, action) => {
        state.user = action.payload.user
        state.session = action.payload.session
        state.isAuthenticated = true
        state.role = action.payload.role
        state.isLoading = false
        state.error = null
        // sessionVersion increments via setSession from onAuthStateChange
      })
      .addCase(loginWithOtp.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(register.fulfilled, (state, action) => {
        state.user = action.payload.user
        state.session = action.payload.session
        state.isAuthenticated = true
        state.role = action.payload.user?.user_metadata?.role || null
        state.isLoading = false
        state.error = null
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.session = null
        state.isAuthenticated = false
        state.role = null
        state.isLoading = false
        state.error = null
        state.lastUsedTeamId = null
        storage.removeItem(LAST_USED_TEAM_STORAGE_KEY)
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Check session
      .addCase(checkSession.pending, (state) => {
        state.isLoading = true
      })
      .addCase(checkSession.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user
          state.session = action.payload.session
          state.isAuthenticated = true
          state.role = action.payload.role
          state.sessionVersion += 1
        } else {
          state.user = null
          state.session = null
          state.isAuthenticated = false
          state.role = null
        }
        state.isLoading = false
      })
      .addCase(checkSession.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Reset password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { setSession, clearError, setLastUsedTeam, setPasswordRecovery } = authSlice.actions
export default authSlice.reducer
